// Shared Atlassian Document Format (ADF) → HTML converter
// Used by both JiraIssueDetail and ConfluencePageDetail

export type AdfAttachment = {
  id: string;
  filename: string;
  mimeType: string;
  content: string;
  thumbnail?: string;
};

export function escapeHtml(str: string): string {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function isAdf(val: any): boolean {
  return val && typeof val === 'object' && val.type === 'doc' && Array.isArray(val.content);
}

export function adfToHtml(node: any, attachments?: AdfAttachment[]): string {
  if (!node || typeof node !== 'object') return '';
  if (typeof node === 'string') return escapeHtml(node);

  if (node.type === 'text') {
    let text = escapeHtml(node.text || '');
    if (node.marks) {
      for (const mark of node.marks) {
        switch (mark.type) {
          case 'strong': text = `<strong>${text}</strong>`; break;
          case 'em': text = `<em>${text}</em>`; break;
          case 'code': text = `<code>${text}</code>`; break;
          case 'strike': text = `<s>${text}</s>`; break;
          case 'underline': text = `<u>${text}</u>`; break;
          case 'link': text = `<a href="${escapeHtml(mark.attrs?.href || '')}" target="_blank" rel="noopener noreferrer">${text}</a>`; break;
          case 'textColor': text = `<span style="color:${escapeHtml(mark.attrs?.color || '')}">${text}</span>`; break;
          case 'subsup': text = mark.attrs?.type === 'sub' ? `<sub>${text}</sub>` : `<sup>${text}</sup>`; break;
        }
      }
    }
    return text;
  }

  const children = (node.content || []).map((c: any) => adfToHtml(c, attachments)).join('');

  switch (node.type) {
    case 'doc': return children;
    case 'paragraph': return `<p>${children || '<br/>'}</p>`;
    case 'heading': return `<h${node.attrs?.level || 3}>${children}</h${node.attrs?.level || 3}>`;
    case 'bulletList': return `<ul>${children}</ul>`;
    case 'orderedList': return `<ol>${children}</ol>`;
    case 'listItem': return `<li>${children}</li>`;
    case 'taskList': return `<ul class="task-list">${children}</ul>`;
    case 'taskItem': {
      const checked = node.attrs?.state === 'DONE';
      return `<li class="flex items-start gap-1.5"><input type="checkbox" disabled ${checked ? 'checked' : ''} class="mt-0.5" /><span>${children}</span></li>`;
    }
    case 'blockquote': return `<blockquote class="border-l-2 border-slate-400 pl-3 my-1 text-slate-400 italic">${children}</blockquote>`;
    case 'codeBlock': {
      const lang = node.attrs?.language || '';
      return `<pre data-lang="${escapeHtml(lang)}"><code>${children}</code></pre>`;
    }
    case 'rule': return '<hr class="my-2 border-slate-600" />';
    case 'hardBreak': return '<br />';
    case 'table': return `<table class="text-xs border-collapse w-full">${children}</table>`;
    case 'tableRow': return `<tr>${children}</tr>`;
    case 'tableHeader': return `<th class="border border-slate-600 px-2 py-1 bg-slate-700/40 font-semibold text-left">${children}</th>`;
    case 'tableCell': return `<td class="border border-slate-600 px-2 py-1">${children}</td>`;
    case 'emoji': return node.attrs?.text || node.attrs?.shortName || '';
    case 'mention': return `<span class="text-blue-400">@${escapeHtml(node.attrs?.text || node.attrs?.id || '')}</span>`;
    case 'inlineCard':
    case 'blockCard':
    case 'embedCard': {
      const url = escapeHtml(node.attrs?.url || '');
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-sky-400 hover:text-sky-300">${url || 'link'}</a>`;
    }
    case 'mediaGroup':
    case 'mediaSingle': return `<div class="my-1">${children}</div>`;
    case 'media': {
      const attId = node.attrs?.id;
      const att = attId && attachments?.find(a => a.id === attId);
      if (att && att.mimeType?.startsWith('image/')) {
        return `<img src="${escapeHtml(att.content)}" alt="${escapeHtml(att.filename)}" class="max-w-full rounded-lg" />`;
      } else if (att && att.mimeType?.startsWith('video/')) {
        return `<embed src="${escapeHtml(att.content)}" type="${escapeHtml(att.mimeType)}" />`;
      } else if (att) {
        return `<a href="${escapeHtml(att.content)}" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 text-xs">📎 ${escapeHtml(att.filename)}</a>`;
      }
      return attId ? `<img data-attachment-id="${escapeHtml(attId)}" alt="attachment" class="max-w-full rounded-lg" />` : '';
    }
    case 'panel': {
      const panelType = node.attrs?.panelType || 'info';
      const colorMap: Record<string, string> = {
        info: 'border-blue-400 bg-blue-900/10',
        note: 'border-amber-400 bg-amber-900/10',
        warning: 'border-red-400 bg-red-900/10',
        success: 'border-green-400 bg-green-900/10',
        error: 'border-red-500 bg-red-900/20',
      };
      const cls = colorMap[panelType] || 'border-blue-400 bg-blue-900/10';
      return `<div class="border-l-2 ${cls} pl-3 my-2 p-2 rounded">${children}</div>`;
    }
    case 'expand':
    case 'nestedExpand': return `<details class="my-1"><summary class="cursor-pointer text-sky-400 text-xs font-medium">${escapeHtml(node.attrs?.title || 'Expand')}</summary><div class="pl-3">${children}</div></details>`;
    case 'layoutSection': return `<div class="my-2">${children}</div>`;
    case 'layoutColumn': return `<div>${children}</div>`;
    case 'status': return `<span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 font-medium">${escapeHtml(node.attrs?.text || '')}</span>`;
    case 'date': {
      const ts = node.attrs?.timestamp;
      return `<span class="text-slate-400">${ts ? new Date(Number(ts)).toLocaleDateString() : ''}</span>`;
    }
    default: return children;
  }
}
